import { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { QrReader } from 'react-qr-reader';
import { db, SYNC_STATUS } from '../db/shiksha-db';
import { QrCode, Camera, ArrowLeft, RefreshCw, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * OpticalSyncHub — Air-gapped data transfer via QR codes
 * Implements Send (QR loop) and Receive (Camera scan)
 */
export function OpticalSyncHub() {
  const [mode, setMode] = useState('send'); // 'send' or 'receive'
  
  return (
    <div className="min-h-screen bg-paper pb-8">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/teacher/dashboard"
            className="p-2 rounded-lg bg-white border border-divider hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft size={20} className="text-ink" />
          </Link>
          <h1 className="text-2xl font-bold text-ink">Optical Sync Hub</h1>
        </div>

        {/* ── Mode Toggle ── */}
        <div className="flex bg-gray-100 p-1 rounded-xl mb-8">
          <button
            onClick={() => setMode('send')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-bold transition-all ${
              mode === 'send' 
                ? 'bg-white text-indigo-700 shadow-sm' 
                : 'text-gray-600 hover:text-ink hover:bg-gray-200/50'
            }`}
          >
            <QrCode size={18} />
            Send Data
          </button>
          <button
            onClick={() => setMode('receive')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-bold transition-all ${
              mode === 'receive' 
                ? 'bg-white text-indigo-700 shadow-sm' 
                : 'text-gray-600 hover:text-ink hover:bg-gray-200/50'
            }`}
          >
            <Camera size={18} />
            Receive Data
          </button>
        </div>

        {/* ── Active Mode View ── */}
        <div className="bg-white border border-divider rounded-xl p-6 shadow-sm">
          {mode === 'send' ? <SendMode /> : <ReceiveMode />}
        </div>
      </div>
    </div>
  );
}

function SendMode() {
  const [chunks, setChunks] = useState([]);
  const [currentChunk, setCurrentChunk] = useState(0);
  const [highDensity, setHighDensity] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load pending sync queue from Dexie
  useEffect(() => {
    async function prepareData() {
      try {
        const queue = await db.syncQueue.toArray();
        if (queue.length === 0) {
          setChunks([]);
          setLoading(false);
          return;
        }

        // Convert queue to JSON and chunk it
        const payloadStr = JSON.stringify({
          source: 'shiksha-edge-peer',
          timestamp: Date.now(),
          records: queue
        });
        
        // Chunk size: ~200 chars for low density, ~500 for high density
        const chunkSize = highDensity ? 500 : 200;
        const totalChunks = Math.ceil(payloadStr.length / chunkSize);
        
        const newChunks = [];
        for (let i = 0; i < totalChunks; i++) {
          const chunkData = payloadStr.substring(i * chunkSize, (i + 1) * chunkSize);
          newChunks.push(JSON.stringify({ c: i+1, t: totalChunks, d: chunkData }));
        }
        
        setChunks(newChunks);
      } catch (err) {
        console.error("Error preparing sync payload", err);
      } finally {
        setLoading(false);
      }
    }
    
    prepareData();
  }, [highDensity]);

  // QR Code animation loop
  useEffect(() => {
    if (chunks.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentChunk((prev) => (prev + 1) % chunks.length);
    }, 500); // 500ms per frame
    
    return () => clearInterval(interval);
  }, [chunks]);

  if (loading) return <div className="py-12 text-center text-graphite">Preparing data...</div>;

  if (chunks.length === 0) {
    return (
      <div className="py-12 text-center flex flex-col items-center">
        <CheckCircle size={48} className="text-green-500 mb-4" />
        <h3 className="text-lg font-bold text-ink">Everything is synced!</h3>
        <p className="text-sm text-graphite mt-1 font-secondary">No pending updates found on this device.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center">
      <h2 className="text-lg font-bold text-ink mb-2">Displaying {chunks.length} QR Codes</h2>
      <p className="text-sm text-graphite mb-6 text-center max-w-sm font-secondary">
        Point the receiving device's camera at this screen. The codes will loop automatically.
      </p>
      
      <div className="p-4 bg-white border-2 border-indigo-100 rounded-xl mb-6">
        <QRCodeSVG 
          value={chunks[currentChunk]} 
          size={256}
          level={highDensity ? 'L' : 'M'}
        />
      </div>
      
      <div className="flex items-center gap-2 text-sm font-medium text-action mb-6">
        <RefreshCw size={16} className="animate-spin-slow" />
        Frame {currentChunk + 1} of {chunks.length}
      </div>
      
      <button 
        onClick={() => setHighDensity(!highDensity)}
        className="text-indigo-600 font-semibold underline text-sm hover:text-indigo-800"
      >
        {highDensity ? "Decrease QR Density" : "Increase QR Density (Faster)"}
      </button>
    </div>
  );
}

function ReceiveMode() {
  const [success, setSuccess] = useState(false);
  const [scannedRecords, setScannedRecords] = useState(0);
  const [receivedChunks, setReceivedChunks] = useState({});
  const [totalExpected, setTotalExpected] = useState(null);
  
  const handleResult = async (result, error) => {
    if (result && !success) {
      try {
        const text = result?.text;
        if (!text || !text.startsWith('{"c":')) return;
        
        const chunk = JSON.parse(text);
        if (chunk.c && chunk.t && chunk.d) {
          setTotalExpected(chunk.t);
          
          setReceivedChunks(prev => {
            const newChunks = { ...prev, [chunk.c]: chunk.d };
            
            // Check if we have all chunks
            if (Object.keys(newChunks).length === chunk.t) {
              // Assemble payload
              let assembledData = '';
              for (let i = 1; i <= chunk.t; i++) {
                assembledData += newChunks[i];
              }
              
              processPayload(assembledData);
            }
            return newChunks;
          });
        }
      } catch (err) {
        // Ignore parsing errors for partial scans
      }
    }
  };

  const processPayload = async (jsonString) => {
    try {
      const payload = JSON.parse(jsonString);
      if (payload.source === 'shiksha-edge-peer' && payload.records) {
        let mergedCount = 0;
        
        for (const record of payload.records) {
          // record is from syncQueue (table, recordId, operation, payload, timestamp)
          if (record.table === 'marks') {
            const existing = await db.marks.get(record.recordId);
            if (!existing || record.timestamp > (existing.syncTimestamp || 0)) {
              await db.marks.put({
                ...record.payload,
                id: record.recordId,
                syncTimestamp: record.timestamp,
                syncStatus: SYNC_STATUS.QUEUED // Needs to sync to cloud still
              });
              mergedCount++;
            }
          } else if (record.table === 'attendance') {
            const existing = await db.attendance.get(record.recordId);
            if (!existing || record.timestamp > (existing.syncTimestamp || 0)) {
              await db.attendance.put({
                ...record.payload,
                id: record.recordId,
                syncTimestamp: record.timestamp,
                syncStatus: SYNC_STATUS.QUEUED
              });
              mergedCount++;
            }
          }
          
          // Also add to this device's sync queue so it can push to cloud
          await db.syncQueue.put({
            ...record,
            id: undefined // Let auto-increment assign a new ID for this device's queue
          });
        }
        
        setScannedRecords(mergedCount);
        setSuccess(true);
      }
    } catch (err) {
      console.error("Failed to process assembled payload", err);
    }
  };

  if (success) {
    return (
      <div className="py-12 text-center flex flex-col items-center">
        <CheckCircle size={48} className="text-green-500 mb-4" />
        <h3 className="text-lg font-bold text-ink">Sync Complete!</h3>
        <p className="text-sm text-graphite mt-1 mb-6 font-secondary">
          Successfully merged {scannedRecords} updates from Peer.
        </p>
        <button 
          onClick={() => {
            setSuccess(false);
            setReceivedChunks({});
            setTotalExpected(null);
          }}
          className="bg-indigo-600 text-white rounded-lg px-6 py-2 text-sm font-semibold hover:bg-indigo-700"
        >
          Scan Another
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center">
      <h2 className="text-lg font-bold text-ink mb-2">Ready to Scan</h2>
      <p className="text-sm text-graphite mb-6 text-center max-w-sm font-secondary">
        Point your camera at the sending device's screen to capture the animated QR codes.
      </p>
      
      <div className="w-full max-w-sm aspect-square bg-black rounded-xl overflow-hidden relative border-4 border-indigo-100">
        <QrReader
          onResult={handleResult}
          constraints={{ facingMode: 'environment' }}
          containerStyle={{ width: '100%', height: '100%' }}
        />
        {/* Viewfinder frame overlay */}
        <div className="absolute inset-0 border-4 border-indigo-500 opacity-50 m-8 rounded-lg pointer-events-none"></div>
      </div>
    </div>
  );
}
